import { CircularProgress, IconButton, TextField } from '@mui/material';
import { Box } from '@mui/system';
import DeleteIcon from '@mui/icons-material/Delete';
import React, { useCallback, useEffect, useState } from 'react';
import { container } from 'tsyringe';
import { Poll, PollInstanceStatus } from '../../../models/poll';
import { PTApiService } from '../../../services/ptapi-service';
import './Polls.scss';
import { compare } from '../../../utils/helpers';
import { PollService } from '../../../services/poll-service';
import { Add, Edit, OpenInNew } from '@mui/icons-material';
import { filter } from 'rxjs/operators';
import InputAdornment from '@mui/material/InputAdornment';
import { AutoHeightStyledTabs, StyledTab, TabPanel } from '../../tabs/StyledTabs';
import { CircleOutlined } from '@mui/icons-material';
import { ShareLinkBubble } from '../../shareLink/shareLinkBubble';
import ShareLink from '../../shareLink/shareLink';
import { StudioProgressBar } from '../../progress-bar/ProgressBar';
import { ErrorService } from '../../../services/error-service';
import { ApiService } from '../../../services/api-service';

const PollOption = ({ optionText, displayVotes, votes, isVoted }: { optionText: string, displayVotes: boolean, votes?: number, isVoted?: boolean }) => {
    return <TextField className={`option-field studio-textfield ${isVoted ? 'voted' : ''}`} variant="outlined" value={optionText} disabled={true}
        InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                    <CircleOutlined />
                </InputAdornment>
            ),
            endAdornment: displayVotes ? (
                <InputAdornment position="end">
                    <Box>({votes})</Box>
                </InputAdornment>
            ) : undefined
        }}
    />;
};

const PollOptionResult = ({ optionText, votes, isVoted, votesPercent }: { optionText: string, votes?: number, isVoted?: boolean, votesPercent?: number }) => {
    return <Box width="100%" display="flex" flexDirection="column" alignItems="flex-start" justifyContent="flex-start" marginBottom={1}>
        <Box className="option-result-name" width="100%" display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
            <Box>{optionText}</Box>
            <Box>{votesPercent * 100}% ({votes} votes)</Box>
        </Box>
        <Box paddingTop={1} width="100%">
            <StudioProgressBar variant="determinate" value={votesPercent * 100} />
        </Box>
    </Box>;
};

const PollDetails = ({ poll, updateStatus, editPoll, deletePoll, canBeStarted, pollIndex, totalPollsCount, shareLinkUrl }: { poll: Poll, updateStatus: (poll: Poll, newStatus: 'ACTIVE' | 'DONE') => any, editPoll: (poll: Poll) => any, deletePoll: (poll: Poll) => any, canBeStarted: boolean, pollIndex: number, totalPollsCount: number, shareLinkUrl: string }) => {
    const [showShareLinkBubble, setShowShareLinkBubble] = useState(false);

    return <Box textAlign="left" padding={2} margin={2} display="flex" flexDirection="column" className={`poll-item-container ${poll.status === PollInstanceStatus.ACTIVE ? 'active-poll' : (poll.status === 'DONE' ? 'completed-poll' : 'available-poll')}`}>
        <Box display="flex" justifyContent="flex-start" alignItems="center" marginBottom={1}>
            <Box marginRight={0.5} className="poll-status-badge">{poll.status === 'ACTIVE' ? 'Live' : (poll.status === 'DONE' ? 'Completed' : 'Ready')}</Box>
            <Box flexGrow="1"></Box>
            <Box>Poll {pollIndex + 1} of {totalPollsCount}</Box>
        </Box>
        <Box marginTop={1} marginBottom={3} fontWeight="bold" fontSize="18px">{poll.question}</Box>
        <Box>
            {!poll.results && poll.options && poll.options.map(o => <Box key={o.option} display="flex" marginBottom={1}>
                <PollOption optionText={o.option} displayVotes={false}></PollOption>
            </Box>)}

            {poll.results && Object.values(poll.results).map(o => <Box key={o.option} display="flex" marginBottom={1}>
                {/* <PollOption optionText={o.option} displayVotes={true} votes={o.votes} isVoted={o.votes !== 0 && o.votes === Math.max(...Object.values(poll.results).map(r => r.votes))}></PollOption> */}
                <PollOptionResult optionText={o.option} votes={o.votes} isVoted={o.votes !== 0 && o.votes === Math.max(...Object.values(poll.results).map(r => r.votes))} votesPercent={o.votes / (Object.values(poll.results).map(r => r.votes).reduce((acc, val) => acc += val, 0) || 1)}></PollOptionResult>
            </Box>)}
        </Box>
        <Box display="flex" justifyContent="flex-start" alignItems="center" marginBottom={1} minHeight="50px">
            <Box className="action-buttons" display="flex" justifyContent="flex-start" alignItems="center">
                {!poll.status && <IconButton size="small" onClick={() => deletePoll(poll)}>
                    <DeleteIcon></DeleteIcon>
                </IconButton>}
                {!poll.status && <IconButton size="small" onClick={() => editPoll(poll)}>
                    <Edit></Edit>
                </IconButton>}
                {poll.status === 'ACTIVE' && <Box position="relative">
                    <IconButton size="small" onClick={() => setShowShareLinkBubble(true)}>
                        <OpenInNew></OpenInNew>
                    </IconButton>
                    <ShareLinkBubble show={showShareLinkBubble} width={370} height={120} onClose={() => setShowShareLinkBubble(false)}>
                        <Box className="share-link-bubble" display="flex" flexDirection="column" justifyContent="center" alignItems="flex-start">
                            <div className="bubble-title">Share your poll with anyone outside the event</div>
                            <ShareLink url={shareLinkUrl}></ShareLink>
                            <a className="share-link" href={shareLinkUrl} target="_blank" rel="noreferrer">
                                <Box marginBottom={2} paddingLeft={1} alignItems="center" justifyContent="flex-start" display="flex" lineHeight="24px">
                                    <OpenInNew />
                                    <Box>View poll engagement</Box>
                                </Box>
                            </a>
                        </Box>
                    </ShareLinkBubble>
                </Box>}
            </Box>
            <Box flexGrow="1"></Box>
            <Box>
                {!poll.status && <button disabled={!canBeStarted} className="studio-primary-btn start-poll-btn" onClick={() => canBeStarted && updateStatus(poll, 'ACTIVE')}>Start poll</button>}
                {poll.status === 'ACTIVE' && <button className="studio-danger-btn end-poll-btn" onClick={() => updateStatus(poll, 'DONE')}>End poll</button>}
            </Box>
        </Box>
    </Box >
};

const PollForm = ({ poll, isEdit, onSave, onCancel }: { poll: Poll, isEdit: boolean, onSave: () => any, onCancel: () => any }) => {

    const [options, setOptions] = useState([...poll.options]);
    const [question, setQuestion] = useState(poll.question);
    const [isFormValid, setIsFormValid] = useState(false);

    useEffect(() => {
        if (question && options && options.length >= 2 && !options.find(o => !o.option)) {
            setIsFormValid(true);
        } else {
            setIsFormValid(false);
        }
    }, [options, question]);

    const updateOptions = useCallback((opt: { option: string, optionNumber: number }[]) => {
        const newOptions = opt.map(o => Object.assign({}, o)).sort((a, b) => compare(a.optionNumber, b.optionNumber, true));
        setOptions(newOptions);
        poll.options = newOptions;
    }, [options]);

    const removeOption = useCallback((i: number) => {
        const newOptions = options.filter(o => o.optionNumber !== i).map((o, i) => ({
            option: o.option,
            optionNumber: i + 1
        }));
        updateOptions(newOptions);
    }, [poll, options, updateOptions]);

    const addOption = useCallback(() => {
        const newOptions = [...options, {
            option: '',
            optionNumber: options.length + 1
        }];
        updateOptions(newOptions);
    }, [options]);

    const updateOption = useCallback((opt: { option: string, optionNumber: number }, newValue: string) => {
        const existingOption = options.find(o => o.optionNumber === opt.optionNumber);
        existingOption.option = newValue;
        updateOptions(options);
    }, [options]);

    return <form className="poll-form" onSubmit={e => { e.preventDefault(); e.stopPropagation(); }}>
        <Box margin={2} display="flex" flexDirection="column" border="1.4px solid #cbd2da" borderRadius="5.6px">
            <Box padding={2} paddingTop={4} paddingBottom={4} borderBottom="1.4px solid #cbd2da" fontSize="18px" fontWeight="bold" textAlign="left" color="#3e434a">
                {isEdit ? 'Edit Poll' : 'Create A Poll'}
            </Box>
            <Box padding={2}>
                <Box marginBottom={1}>
                    <TextField className="question-field studio-textfield" placeholder="Question" variant="outlined" value={question} multiline={true} onChange={(e) => { setQuestion(e.target.value); poll.question = e.target.value; }} />
                </Box>
                {options.map((o, i) => <Box key={i} display="flex" marginBottom={1}>
                    <TextField className="option-field studio-textfield" placeholder={`Option ${o.optionNumber}`} variant="outlined" value={o.option} onChange={(e) => { updateOption(o, e.target.value); }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CircleOutlined />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {i > 1 && <>
                        <Box width="8px"></Box>
                        <IconButton size="small" className="delete-icon" onClick={() => removeOption(o.optionNumber)}>
                            <DeleteIcon></DeleteIcon>
                        </IconButton>
                    </>}
                </Box>)}
                <Box textAlign="left">
                    <a className="add-link" href="#" onClick={(e) => { e.preventDefault(); addOption(); }}>
                        <Box alignItems="center" justifyContent="flex-start" display="flex" lineHeight="24px">
                            <Add />
                            <Box>Add option</Box>
                        </Box>
                    </a>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="flex-end" paddingTop={1}>
                    <button className="studio-cancel-btn poll-form-btn" onClick={() => onCancel()}>Cancel</button>
                    <button className="studio-primary-btn poll-form-btn" disabled={!isFormValid} onClick={() => { if (isFormValid) { onSave(); } }}>Save</button>
                </Box>
            </Box>
        </Box>

    </form>
};

const NoPoll = ({ mode, startCreatePoll }: { mode: 'active' | 'completed', startCreatePoll: () => any }) => {
    return <Box className="no-poll-container" height="100%">
        <Box paddingTop={7} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
            <div className="no-poll-icon"></div>
            <Box marginTop={2}>There is no {mode} poll</Box>
            <Box marginTop={2}>
                <button className="studio-primary-btn create-poll-btn" onClick={() => startCreatePoll()}>Create a poll</button>
            </Box>
        </Box>
    </Box>;
};

export const Polls = () => {

    const [errorService] = useState(container.resolve(ErrorService))
    const [pollService] = useState(container.resolve(PollService));
    const [ptApiService] = useState(container.resolve(PTApiService));
    const [apiService] = useState(container.resolve(ApiService));
    const [polls, setPolls] = useState<Poll[]>(null);
    const [availableActivePolls, setAvailableActivePolls] = useState<Poll[]>(null);
    const [completedPolls, setCompletedPolls] = useState<Poll[]>(null);
    const [createPoll, setCreatePoll] = useState(false);
    const [editPoll, setEditPoll] = useState(false);
    const [currentPoll, setCurrentPoll] = useState<Poll>();
    const [isSendingRequest, setIsSendingRequest] = useState(false);
    const [showShareLinkBubble, setShowShareLinkBubble] = useState(false);
    const [pollShortUrl, setPollShortUrl] = useState('');
    const [eventInstanceId, setEventInstanceId] = useState<string>(null);
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        pollService.fetchPolls();
    };

    useEffect(() => {
        // const sub = ptApiService.ptCurrentEventInformation$.pipe(
        //     filter(a => a && a.length > 1 && !!a[0].instanceId)
        // ).subscribe(([e1]) => setEventInstanceId(e1.instanceId));

        // return () => sub.unsubscribe();

        setPolls([
            // set it empty
            // {
            // question: "Do you like it?",
            // options: [
            //         { option: "yes", optionNumber: 1 },
            //         { option: "no", optionNumber: 2 },
            //         { option: "don't care", optionNumber: 3 }
            //     ],
            // eventId: "8acc1ed5-da83-459d-b97e-07c64a18fae5",
            // pollGuid: "7e2dcb61-3f05-450a-bbd5-b10c51cc6de7",
            // creatorName: null,
            // creatorGuid: null
            // }
        ]);
    }, []);

    // Wait to have an event instance id before pulling polls
    useEffect(() => {
        if (!eventInstanceId) {
            return;
        }

        const subs = [
            pollService.pollsObservable.subscribe(newPolls => {
                setPolls(newPolls);
                pollService.markPollIdsAsSeen(newPolls.map(p => p.pollGuid))
            }),
            ptApiService.ptCurrentEventInformation$.pipe(filter(a => a && a.length > 1)).subscribe(([e1]) => setPollShortUrl(e1.pollShortUrl))
        ];

        pollService.fetchPolls();
        return () => subs.forEach(s => s.unsubscribe());
    }, [eventInstanceId]);

    useEffect(() => {
        if (polls !== null) {
            setAvailableActivePolls(polls.filter(p => p.status !== 'DONE'));
            setCompletedPolls(polls.filter(p => p.status === 'DONE'));
        }
    }, [polls]);

    const startCreatePoll = () => {
        setCurrentPoll({
            pollGuid: '',
            question: '',
            options: [{
                optionNumber: 1,
                option: ''
            }, {
                optionNumber: 2,
                option: ''
            }]
        });
        setCreatePoll(true);
    };

    const doCreatePoll = useCallback(async () => {
        apiService.toggleRegisterPopup();
        // setIsSendingRequest(true);

        // try {
        //     // await ptApiService.createPoll({
        //     //     question: currentPoll.question,
        //     //     options: currentPoll.options
        //     // });
        //     // setCreatePoll(false);
        // } catch (e) {
        //     console.error(e);
        //     errorService.throwError(e?.message || e);
        // } finally {
        //     setIsSendingRequest(false);
        // }
    }, [currentPoll, setCreatePoll]);

    const doEditPoll = useCallback(async () => {
        setIsSendingRequest(true);

        try {
            // await ptApiService.editPoll(currentPoll.pollGuid, {
            //     options: currentPoll.options,
            //     question: currentPoll.question
            // });
            setEditPoll(false);
        } catch (e) {
            console.error(e);
            errorService.throwError(e?.message || e);
        } finally {
            setIsSendingRequest(false);
        }
    }, [currentPoll, setEditPoll]);

    const updatePollStatus = async (poll: Poll, newStatus: 'ACTIVE' | 'DONE') => {
        setIsSendingRequest(true);
        try {
            if (newStatus === 'ACTIVE') {
                // await ptApiService.startPoll(poll.pollGuid);
            } else if (newStatus === 'DONE') {
                // await ptApiService.endPoll(poll.pollGuid, poll.pollInstanceGuid);
            }
        } catch (e) {
            console.error(e);
            errorService.throwError(e?.message || e);
        } finally {
            setIsSendingRequest(false);
        }
    };

    const deletePoll = async (poll: Poll) => {
        setIsSendingRequest(true);
        try {
            // await ptApiService.deletePoll(poll.pollGuid);
        } catch (e) {
            console.error(e);
            errorService.throwError(e?.message || e);
        } finally {
            setIsSendingRequest(false);
        }
    };

    const startEditPoll = (poll: Poll) => {
        setCurrentPoll(poll);
        setEditPoll(true);
    };

    return <Box paddingTop={1} display="flex" flexDirection="column" className="polls-container">
        {!createPoll && !editPoll && <>
            <Box sx={{ bgcolor: '#ffffff', width: '100%' }}>
                <AutoHeightStyledTabs
                    value={tabValue}
                    onChange={handleTabChange}
                >
                    <StyledTab label="Active" />
                    <StyledTab label="Completed" />
                </AutoHeightStyledTabs>
            </Box>
            <TabPanel className="full-height-tab" value={tabValue} index={0}>
                {availableActivePolls !== null && <>
                    {availableActivePolls.length === 0 && <NoPoll startCreatePoll={startCreatePoll} mode='active' />}
                    {availableActivePolls.length > 0 && <Box textAlign="left">
                        {availableActivePolls.map((p, i) => <PollDetails key={p.pollGuid} poll={p} updateStatus={(p, s) => updatePollStatus(p, s)} deletePoll={p => deletePoll(p)} editPoll={p => startEditPoll(p)} canBeStarted={!p.status && !availableActivePolls.find(pp => pp.status === 'ACTIVE')} pollIndex={i} totalPollsCount={availableActivePolls.length} shareLinkUrl={pollShortUrl}></PollDetails>)}
                        <a className="add-link" href="#" onClick={(e) => { e.preventDefault(); startCreatePoll(); }}>
                            <Box marginBottom={2} paddingLeft={1} alignItems="center" justifyContent="flex-start" display="flex" lineHeight="24px">
                                <Add />
                                <Box>Create a new poll</Box>
                            </Box>
                        </a>
                    </Box>}
                </>}
                {availableActivePolls === null && <Box display="flex" width="100%" alignItems="center" justifyContent="center" paddingTop={3}>
                    <CircularProgress />
                </Box>}
            </TabPanel>
            <TabPanel className="full-height-tab" value={tabValue} index={1}>
                {completedPolls !== null && <>
                    {completedPolls.length === 0 && <NoPoll startCreatePoll={startCreatePoll} mode='completed' />}
                    {completedPolls.length > 0 && <Box textAlign="left">
                        {completedPolls.map((p, i) => <PollDetails key={p.pollGuid} poll={p} updateStatus={(p, s) => updatePollStatus(p, s)} deletePoll={p => deletePoll(p)} editPoll={p => startEditPoll(p)} canBeStarted={!p.status && !completedPolls.find(pp => pp.status === 'ACTIVE')} pollIndex={i} totalPollsCount={completedPolls.length} shareLinkUrl={pollShortUrl}></PollDetails>)}
                        <a className="add-link" href="#" onClick={(e) => { e.preventDefault(); startCreatePoll(); }}>
                            <Box marginBottom={2} paddingLeft={1} alignItems="center" justifyContent="flex-start" display="flex" lineHeight="24px">
                                <Add />
                                <Box>Create a new poll</Box>
                            </Box>
                        </a>
                    </Box>}
                </>}
                {completedPolls === null && <Box display="flex" width="100%" alignItems="center" justifyContent="center" paddingTop={3}>
                    <CircularProgress />
                </Box>}
            </TabPanel>
        </>}
        {createPoll && <PollForm poll={currentPoll} isEdit={false} onCancel={() => setCreatePoll(false)} onSave={() => doCreatePoll()}></PollForm>}
        {editPoll && <PollForm poll={currentPoll} isEdit={true} onCancel={() => setEditPoll(false)} onSave={() => doEditPoll()}></PollForm>}
    </Box>;
}